/**
 * lib/acquisition-source.ts — which external platform a visit arrived from
 * (FRIDGE-1223). Reporting label only.
 *
 * Why a cookie: middleware 301-strips `utm_source` (duplicate-URL hygiene), and
 * by the time a visitor clicks "See Deal" the /r/ request's Referer is our own
 * page. The browser keeps `document.referrer` across that 301, so the landing
 * page records the platform once in a first-party cookie and /r/ copies it into
 * `clicks.channel_context.acquisition`.
 *
 * It is NOT the signed FRIDGE-818 `channel`: nothing here is verified, it never
 * touches the destination, subid or signature verdict, and it carries no
 * identifier — only an allowlisted platform name.
 */

export const ACQUISITION_COOKIE = 'fc_acq'
export const ACQUISITION_MAX_AGE_SECONDS = 30 * 24 * 60 * 60

export type AcquisitionSource = 'pinterest'
const SOURCES: ReadonlySet<string> = new Set<AcquisitionSource>(['pinterest'])

function isPinterestHost(host: string): boolean {
  // pinterest.com, www.pinterest.com, pinterest.co.uk, nl.pinterest.com, pin.it
  return host === 'pin.it' || /(^|\.)pinterest\.(?:[a-z]{2,3})(?:\.[a-z]{2})?$/.test(host)
}

/** The platform a referrer URL names, or null for anything unrecognised. */
export function acquisitionFromReferrer(referrer: string | null | undefined): AcquisitionSource | null {
  if (!referrer) return null
  let host: string
  try {
    const url = new URL(referrer)
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null
    host = url.hostname.toLowerCase()
  } catch {
    return null
  }
  return isPinterestHost(host) ? 'pinterest' : null
}

/** The allowlisted value of the acquisition cookie in a Cookie header, or null. */
export function readAcquisitionCookie(cookieHeader: string | null | undefined): AcquisitionSource | null {
  if (!cookieHeader) return null
  for (const part of cookieHeader.split(';')) {
    const eq = part.indexOf('=')
    if (eq < 0 || part.slice(0, eq).trim() !== ACQUISITION_COOKIE) continue
    const value = part.slice(eq + 1).trim()
    return SOURCES.has(value) ? (value as AcquisitionSource) : null
  }
  return null
}

/** The Set-Cookie string the landing page writes for a recognised source. */
export function acquisitionCookie(source: AcquisitionSource, secure: boolean): string {
  return `${ACQUISITION_COOKIE}=${source}; Max-Age=${ACQUISITION_MAX_AGE_SECONDS}; Path=/; SameSite=Lax${secure ? '; Secure' : ''}`
}
